// ErrorView title/message/button resolution (error_view.dart, spec 01 §10.6),
// accepting Flutter-style AppError shapes and the PWA's AbsError.

export type ErrorRecommendedAction = 'retry' | 'relogin' | 'reDownload' | 'checkConnection' | 'none';

export interface ErrorLike {
  title?: string | null;
  userMessage?: string | null;
  message?: string;
  isRetryable?: boolean;
  recommendedAction?: ErrorRecommendedAction;
  status?: number;
  isUnauthorized?: boolean;
  isNetworkError?: boolean;
}

export interface ErrorContentInput {
  error?: unknown;
  title?: string | null;
  message?: string | null;
  hasRetry: boolean;
  hasRelogin: boolean;
}

export interface ErrorContent {
  title: string;
  message: string;
  showRetry: boolean;
  showRelogin: boolean;
}

const nonBlank = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

export function asErrorLike(error: unknown): ErrorLike | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return { userMessage: error };
  if (typeof error === 'object') return error as ErrorLike;
  return undefined;
}

export interface HttpAppError {
  title: string;
  userMessage: string;
  isRetryable: boolean;
  recommendedAction: ErrorRecommendedAction;
}

/**
 * Flutter's `mapExceptionToAppError` (`_mapHttpStatusToAppError` and the Dio
 * timeout / connection branches) for an HTTP or network error that carries no
 * user-facing copy: the PWA's `AbsError` (an Error with a numeric `status`),
 * whose message is often the server's raw response body.
 */
export function httpAppError(error: unknown): HttpAppError | undefined {
  if (!(error instanceof Error)) return undefined;
  const { status, isNetworkError, userMessage } = error as ErrorLike;
  if (typeof status !== 'number' || nonBlank(userMessage)) return undefined;
  if (isNetworkError === true || status === 0) {
    return /too long|timed? ?out/i.test(error.message)
      ? { title: 'Request timed out', userMessage: 'The server is taking too long to respond. Please try again.', isRetryable: true, recommendedAction: 'retry' }
      : { title: 'No connection', userMessage: 'No network connection is available. Check your internet and retry.', isRetryable: true, recommendedAction: 'checkConnection' };
  }
  if (status === 401 || status === 403) {
    return { title: 'Sign-in required', userMessage: 'Your session has expired. Please sign in again.', isRetryable: false, recommendedAction: 'relogin' };
  }
  if (status === 404) {
    return { title: 'Not found', userMessage: 'The requested item was not found on the server.', isRetryable: false, recommendedAction: 'none' };
  }
  const retryable = status >= 500 || status === 408 || status === 429;
  return {
    title: 'Server error',
    userMessage: retryable ? 'The server is temporarily unavailable. Please retry.' : 'The request failed. Please verify your input and try again.',
    isRetryable: retryable,
    recommendedAction: retryable ? 'retry' : 'none',
  };
}

export function resolveErrorContent({ error, title, message, hasRetry, hasRelogin }: ErrorContentInput): ErrorContent {
  const e = asErrorLike(error);
  const http = httpAppError(error);
  const isRelogin = e?.recommendedAction === 'relogin' || e?.isUnauthorized === true || e?.status === 401 || http?.recommendedAction === 'relogin';
  return {
    title: nonBlank(title) ?? nonBlank(e?.title) ?? http?.title ?? 'Something went wrong',
    message:
      nonBlank(message) ??
      nonBlank(e?.userMessage) ??
      http?.userMessage ??
      nonBlank(error instanceof Error ? error.message : undefined) ??
      'Please try again.',
    showRetry: hasRetry && (e?.isRetryable ?? http?.isRetryable ?? true),
    showRelogin: hasRelogin && isRelogin,
  };
}

/** Multi-line `key=value` dump for the debug "Copy details" button. */
export function errorDetails(error: unknown): string {
  const e = asErrorLike(error);
  const lines: string[] = [];
  const add = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') lines.push(`${key}=${String(value)}`);
  };
  if (e && typeof e === 'object') {
    const record = e as Record<string, unknown>;
    add('category', record.category);
    add('code', record.code);
    add('status', record.status ?? record.statusCode);
    add('retryable', record.isRetryable);
    add('action', record.recommendedAction);
    add('message', record.userMessage ?? record.message);
    add('debug', record.debugMessage);
  }
  if (error instanceof Error) {
    add('exception', `${error.name}: ${error.message}`);
    add('stack', error.stack);
  }
  return lines.join('\n');
}
